import React, { ChangeEventHandler, useRef } from 'react';
import classNames from 'classnames';
import { Stack, Container, WrapItem, Wrap } from '@fiverr-private/layout_components';
import { ShowMoreTag, Tag, TagCollection } from '@fiverr-private/data_display';
import { Text } from '@fiverr-private/typography';
import { TextareaGroup, TextareaSlot, Textarea } from '@fiverr-private/forms';
import { useClickOutside } from '@fiverr-private/hooks';
import { AppI18n } from '../../../../i18n';
import { PROMPT_BOX_TAGS } from '../constants';
import { PromptBoxTagKey } from '../types';
import { ResponsiveCTAButton } from './ResponsiveCTAButton';
import { textAreaExpandedStyles, textAreaStyles } from './styles.ve.css';

// TODO: Refactor when penta will support tag colletion classnames of noWrap property
const TAG_ARIA_LABEL = 'Tag';

const renderTags = ({ onClick, size = 'xs' }: { onClick: (tag: PromptBoxTagKey) => void; size: 'sm' | 'xs' }) =>
    PROMPT_BOX_TAGS.map((tag) => (
        <Tag
            key={tag}
            clickableArea="multi"
            ariaLabel={TAG_ARIA_LABEL}
            size={size}
            onClick={() => onClick(tag)}
            variant="outline"
        >
            <AppI18n k={`prompt_box.tags.${tag}.name`} />
        </Tag>
    ));

interface Props {
    value: string;
    isLoading: boolean;
    isActive: boolean;
    isError: boolean;
    onGenerateBrief: () => void;
    onChange: ChangeEventHandler<HTMLTextAreaElement>;
    onBlur: () => void;
    onFocus: () => void;
    onTagClick: (tag: any) => void;
}

export const PromptTextArea = ({
    value,
    isError,
    isLoading,
    isActive,
    onFocus,
    onChange,
    onGenerateBrief,
    onBlur,
    onTagClick,
}: Props) => {
    const textAreaRef = useRef(null);

    useClickOutside(textAreaRef, (e) => {
        if ((e.target as HTMLElement).getAttribute('aria-label') === TAG_ARIA_LABEL) {
            return;
        }

        onBlur();
    });

    return (
        <>
            <Container position="relative" dropShadow="z4">
                <Container
                    left="0px"
                    top="0px"
                    position="absolute"
                    display="block"
                    background="linear-gradient(90deg, rgba(219, 149, 255, 0.1) 0%, rgba(0, 229, 145, 0.1) 81.25%)"
                    width="100%"
                    height="100%"
                    zIndex="0"
                    style={{ filter: 'blur(20px)' }}
                />
                <Container
                    position="relative"
                    zIndex="1"
                    overflowY={{ default: 'hidden', lg: 'visible' }}
                    ref={textAreaRef}
                    onClick={onFocus}
                    onFocus={onFocus}
                >
                    <Text
                        position="absolute"
                        left="24px"
                        top="19px"
                        color="bodySecondary"
                        pointerEvents="none"
                        textAlign="start"
                        zIndex="1"
                        maxWidth="calc(100% - 40px)"
                        size="b_md"
                        hidden={!!value}
                        display="webkitBox"
                        textOverflow="ellipsis"
                        webkitBoxOrient="vertical"
                        overflow="hidden"
                        webkitLineClamp={1}
                    >
                        <AppI18n
                            k="prompt_box.placeholder"
                            templates={{
                                'no-code': (text) => (
                                    <Text fontWeight="bold" as="span" color="bodyPrimary">
                                        {text}
                                    </Text>
                                ),
                            }}
                        />
                    </Text>
                    <TextareaGroup
                        width="100%"
                        isError={value.length > 0 && !isError}
                        className={classNames(textAreaStyles, isActive && textAreaExpandedStyles)}
                        maxHeight="200px"
                        overflowY={{ default: 'hidden', lg: 'visible' }}
                    >
                        <Textarea
                            width="100%"
                            rows={isActive ? 6 : 1}
                            value={value}
                            onChange={onChange}
                            maxHeight={{ default: '100px', lg: '150px' }}
                        />
                        <TextareaSlot position="bottom" width="100%" hidden={!isActive}>
                            <Stack alignItems="center" justifyContent={{ default: 'flexEnd', lg: 'spaceBetween' }}>
                                <Container hidden={{ default: true, lg: false }} flex="1">
                                    <TagCollection height="32px" size="sm" gap="2" width="100%" variant="outline">
                                        {renderTags({ onClick: onTagClick, size: 'xs' })}
                                        <ShowMoreTag />
                                    </TagCollection>
                                </Container>
                                <ResponsiveCTAButton
                                    disabled={!isError || isLoading}
                                    onClick={onGenerateBrief}
                                    loading={isLoading}
                                />
                            </Stack>
                        </TextareaSlot>
                    </TextareaGroup>
                </Container>
            </Container>
            {value.length > 0 && !isError && value.length < 20 && (
                <Text size="b_xs" color="validationError" alignSelf="flexStart">
                    <AppI18n k="prompt_box.validation.minLength" />
                </Text>
            )}
            <Wrap
                gap="2"
                alignItems="center"
                justifyContent="center"
                marginTop={{ default: '8', lg: '12' }}
                hidden={{ default: !isActive, lg: true }}
            >
                {renderTags({ onClick: onTagClick, size: 'sm' }).map((tag, index) => (
                    <WrapItem key={index}>{tag}</WrapItem>
                ))}
            </Wrap>
        </>
    );
};
